/**
 * The projection's finishing action. Before the plan is finished it is "Finish with self-funding":
 * records flow completion, then opens Your Plan (the first time with the "Your plan is ready"
 * arrival); if the save fails the consumer stays on the projection (the plan hook shows the error).
 * Once the plan is finished, revisiting the projection (to check a change) offers "Back to your plan"
 * instead, which only navigates: there is nothing left to finish.
 */
import { hasReachedFlowCompletion } from '../lib/flow-completion';
import { useConsumerFlow } from './use-consumer-flow';
import { useConsumerPlan } from './use-consumer-plan';
import { useRecordFlowCompletion } from './use-record-flow-completion';
import { PLAN_READY_STATE } from './use-plan-ready-arrival';

export function useFinishWithSelfFunding(clientId: string) {
  const { goTo } = useConsumerFlow(clientId);
  const { plan, applications } = useConsumerPlan(clientId);
  const { recordFlowCompletion, isRecording } = useRecordFlowCompletion(clientId);
  const hasFinished = hasReachedFlowCompletion(plan, applications);

  const finishWithSelfFunding = async () => {
    try {
      const { firstCompletion } = await recordFlowCompletion();
      goTo('your-plan', { state: firstCompletion ? PLAN_READY_STATE : undefined });
    } catch {
      // The plan hook has already told the consumer the save failed; stay here.
    }
  };

  const finishAction = hasFinished
    ? { label: 'Back to your plan', onSelect: () => goTo('your-plan') }
    : { label: 'Finish with self-funding', onSelect: finishWithSelfFunding, busy: isRecording };

  return { finishAction, finishWithSelfFunding, isFinishing: isRecording };
}
